import type { ReactNode } from 'react';
import { useWhoami } from '../api/whoami';
import type { WhoamiEffective } from '../api/whoami';
import { ForbiddenPage } from '../pages/ForbiddenPage';

/**
 * The role names guards can require. Mirrors the boolean flags on
 * `whoami.effective` — gating always evaluates against the *effective* identity
 * (the impersonation target when impersonating), per DDR-PLT-016.
 */
export type AppRole = 'Administrator' | 'Manager' | 'RequestDelegate' | 'SiteAdministrator';

interface RequireRoleProps {
  /** The role (or any-of set of roles) the effective identity must hold. */
  roles: AppRole | AppRole[];
  children: ReactNode;
}

function effectiveHasRole(effective: WhoamiEffective, role: AppRole): boolean {
  switch (role) {
    case 'Administrator':
      return effective.isAdministrator;
    case 'Manager':
      return effective.isManager;
    case 'RequestDelegate':
      return effective.isRequestDelegate;
    case 'SiteAdministrator':
      return effective.isSiteAdministrator;
    default:
      return false;
  }
}

/**
 * Renders its children only when the effective identity holds at least one of
 * the required roles. On an insufficient role it renders the **403 Forbidden
 * page in place** — it does not redirect (WEB PRD §2.2: a page, not a redirect,
 * so the user isn't bounced into a loop and the URL they hit is preserved).
 *
 * While `whoami` is loading it renders nothing; the surrounding `AppShell`
 * chrome stays visible and the query (staleTime ∞) is normally already cached.
 */
export function RequireRole({ roles, children }: RequireRoleProps) {
  const { data, isPending } = useWhoami();

  if (isPending || !data) {
    return null;
  }

  const required = Array.isArray(roles) ? roles : [roles];
  const allowed = required.some((role) => effectiveHasRole(data.effective, role));

  return allowed ? <>{children}</> : <ForbiddenPage />;
}
